import { useState, useCallback } from 'react'

interface DetectedLocation {
  countryCode: string
  countryName: string
  cityName: string
  latitude: number
  longitude: number
  accuracy?: number
}

interface LocationDetectorResult {
  isDetecting: boolean
  detectedLocation: DetectedLocation | null
  error: string | null
  detectLocation: () => Promise<void>
  clearLocation: () => void
}

// BigDataCloud API for reverse geocoding (free, no API key needed for client-side)
const GEOCODING_API = 'https://api.bigdatacloud.net/data/reverse-geocode-client'

export function useLocationDetector(): LocationDetectorResult {
  const [isDetecting, setIsDetecting] = useState(false)
  const [detectedLocation, setDetectedLocation] = useState<DetectedLocation | null>(null)
  const [error, setError] = useState<string | null>(null)

  const clearLocation = useCallback(() => {
    setDetectedLocation(null)
    setError(null)
  }, [])

  const detectLocation = useCallback(async () => {
    setIsDetecting(true)
    setError(null)

    try {
      // Check if Telegram LocationManager is available
      const telegram = window.Telegram?.WebApp
      const locationManager = (telegram as any)?.LocationManager
      if (locationManager?.isInited) {
        // Try Telegram LocationManager first
        locationManager.openLocationAccess({
          onAccessGranted: async (location: any) => {
            try {
              const geocoded = await reverseGeocode(location.latitude, location.longitude)
              if (geocoded) {
                setDetectedLocation({
                  ...geocoded,
                  latitude: location.latitude,
                  longitude: location.longitude,
                  accuracy: location.accuracy
                })
              } else {
                throw new Error('Could not determine city from coordinates')
              }
            } catch (err) {
              console.error('Geocoding error:', err)
              setError('Could not determine your location. Please enter manually.')
            } finally {
              setIsDetecting(false)
            }
          },
          onAccessDenied: () => {
            // Fall back to browser geolocation
            detectWithBrowserGeolocation()
          }
        })
      } else {
        // Use browser geolocation API
        detectWithBrowserGeolocation()
      }
    } catch (err) {
      console.error('Location detection error:', err)
      setError('Location detection failed. Please enter manually.')
      setIsDetecting(false)
    }
  }, [])

  const detectWithBrowserGeolocation = async () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser')
      setIsDetecting(false)
      return
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const geocoded = await reverseGeocode(
            position.coords.latitude,
            position.coords.longitude
          )
          if (geocoded) {
            setDetectedLocation({
              ...geocoded,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            })
          } else {
            throw new Error('Could not determine city from coordinates')
          }
        } catch (err) {
          console.error('Geocoding error:', err)
          setError('Could not determine your location. Please enter manually.')
        } finally {
          setIsDetecting(false)
        }
      },
      (err) => {
        console.error('Geolocation error:', err)
        switch(err.code) {
          case err.PERMISSION_DENIED:
            setError('Location permission denied. Please enable location access.')
            break
          case err.POSITION_UNAVAILABLE:
            setError('Location information unavailable.')
            break
          case err.TIMEOUT:
            setError('Location request timed out.')
            break
          default:
            setError('Unable to determine location.')
        }
        setIsDetecting(false)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    )
  }

  const reverseGeocode = async (latitude: number, longitude: number) => {
    try {
      const response = await fetch(
        `${GEOCODING_API}?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
      )

      if (!response.ok) {
        throw new Error('Geocoding request failed')
      }

      const data = await response.json()

      // Extract city and country information
      const cityName = data.city || data.locality || data.principalSubdivision
      const countryCode = data.countryCode
      const countryName = data.countryName

      if (!cityName || !countryCode) {
        console.warn('Incomplete location data:', data)
        return null
      }

      return {
        cityName,
        countryCode,
        countryName
      }
    } catch (err) {
      console.error('Reverse geocoding error:', err)
      throw err
    }
  }

  return {
    isDetecting,
    detectedLocation,
    error,
    detectLocation,
    clearLocation
  }
}